import React, { useRef, useState } from 'react';

import TpDialog from '../shared/tp_dialog';
import { LIMITS, approval_note_error, approval_view, confirm_copy, text_length } from './logic';

/*
 * PROD-7828 R14 P6 — the approval step of one rule change (maker-checker). The maker asks with a reason;
 * another teacher-payments user approves (or approves and saves) or rejects with a note; the maker may
 * withdraw; the approver may cancel their approval. What the reader may do comes from the server's
 * viewer block; this card words it and collects the note. Reject and Withdraw are terminal, so each
 * asks first.
 * props: change (the change view), busy (the panel's write in flight), deadline (logic.save_deadline),
 * on_request(note), on_approve(note), on_approve_and_save(note), on_reject(note), on_withdraw(),
 * on_cancel_approval().
 */
const NoteField = ({ id, label, value, on_change, error, show_error, input_ref }) => (
  <div className="tp-field">
    <label htmlFor={id}>{label}</label>
    <textarea
      id={id}
      ref={input_ref}
      className="tp-textarea"
      maxLength={LIMITS.approval_note_max_len}
      value={value}
      aria-invalid={show_error && error ? 'true' : undefined}
      aria-describedby={`${id}-hint${show_error && error ? ` ${id}-err` : ''}`}
      onChange={(e) => on_change(e.target.value)}
    />
    <span id={`${id}-hint`} className="tp-hint">
      {text_length(value)}/{LIMITS.approval_note_max_len} characters, at least{' '}
      {LIMITS.approval_note_min_len}.
    </span>
    {show_error && error ? (
      <span id={`${id}-err`} className="tp-err" role="alert">
        {error}
      </span>
    ) : null}
  </div>
);

const ReasonList = ({ items }) => (
  <ul style={{ margin: '6px 0 0 18px' }}>
    {items.map((r) => (
      <li key={r}>{r}</li>
    ))}
  </ul>
);

const Reasons = ({ v }) => (
  <>
    <ReasonList items={v.reasons} />
    {v.changed_text ? (
      <div className="tp-banner tp-banner-warn" role="note" style={{ marginTop: 8 }}>
        <div>{v.changed_text}</div>
        {v.added_reasons.length ? <ReasonList items={v.added_reasons} /> : null}
        <div className="tp-hint" style={{ marginTop: 6 }}>
          When approval was asked: {v.asked_reasons.join(' ')}
        </div>
      </div>
    ) : null}
    {v.impact_unknown ? (
      <div className="tp-banner tp-banner-warn" role="note" style={{ marginTop: 8 }}>
        The preview could not measure how much money this change moves: no open month exists from
        its date yet, or some months could not be priced. Judge it from the rule change itself.
      </div>
    ) : null}
  </>
);

const Asked = ({ v }) =>
  v.asked_line ? (
    <p style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap' }}>
      <span className="tp-strong">{v.asked_line}: </span>
      {v.asked_note}
    </p>
  ) : null;

const Reviewed = ({ v }) =>
  v.reviewed_line ? (
    <p style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap' }}>
      <span className="tp-strong">{v.reviewed_line}: </span>
      {v.review_note}
    </p>
  ) : null;

// The save window, and a warning when under 2 hours are left.
const Deadline = ({ d }) =>
  d && (d.text || d.warn) ? (
    <>
      {d.text ? (
        <div className="tp-hint" data-testid="rx-approval-deadline">
          {d.text}
        </div>
      ) : null}
      {d.warn ? (
        <div className="tp-banner tp-banner-warn" role="note">
          {d.warn}
        </div>
      ) : null}
    </>
  ) : null;

const Confirm = ({ copy, on_keep, on_go }) => (
  <TpDialog title={copy.title} on_close={on_keep}>
    <div className="tp-modal">
      <p style={{ margin: '0 0 12px' }}>{copy.text}</p>
      <div className="tp-modal-ft">
        <button type="button" className="tp-btn-out" onClick={on_keep}>
          {copy.keep}
        </button>
        <button type="button" className="tp-btn" onClick={on_go}>
          {copy.go}
        </button>
      </div>
    </div>
  </TpDialog>
);

const ApprovalCard = ({
  change,
  busy,
  deadline = null,
  on_request,
  on_approve,
  on_approve_and_save,
  on_reject,
  on_withdraw,
  on_cancel_approval
}) => {
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const [confirm, setConfirm] = useState('');
  const note_ref = useRef(null);
  const v = approval_view(change);
  if (!v.mode) return null;
  const error = approval_note_error(note);
  // A short note: say why and put the cursor where the fix goes.
  const note_ok = () => {
    setTried(true);
    if (!error) return true;
    if (note_ref.current) note_ref.current.focus();
    return false;
  };
  const submit = (fn) => {
    if (note_ok()) fn(note.trim());
  };
  const go = () => {
    const kind = confirm;
    setConfirm('');
    if (kind === 'withdraw') on_withdraw();
    else on_reject(note.trim());
  };
  const withdraw = v.can_withdraw ? (
    <button
      type="button"
      className="tp-btn-out"
      disabled={!!busy}
      onClick={() => setConfirm('withdraw')}
    >
      {busy === 'withdraw' ? 'Withdrawing…' : 'Withdraw request'}
    </button>
  ) : null;

  return (
    <section className="tp-card" aria-labelledby="tp-rx-approval">
      <div className="tp-card-hd">
        <span className="tp-card-hd-title" id="tp-rx-approval">
          Approval
        </span>
        <span className="tp-card-hd-sub">
          Another teacher-payments user must approve this change before it is saved.
        </span>
      </div>
      {v.mode === 'request' ? (
        <div className="tp-form">
          <div>This change needs approval because:</div>
          <Reasons v={v} />
          <Deadline d={deadline} />
          {v.can_request ? (
            <>
              <NoteField
                id="rx-approval-reason"
                label="Reason for the approver"
                value={note}
                on_change={setNote}
                error={error}
                show_error={tried}
                input_ref={note_ref}
              />
              <div className="tp-modal-ft">
                <button
                  type="button"
                  className="tp-btn"
                  disabled={!!busy}
                  onClick={() => submit(on_request)}
                >
                  {busy === 'request' ? 'Sending…' : 'Request approval'}
                </button>
              </div>
            </>
          ) : (
            <span className="tp-hint" data-testid="rx-approval-blocker">
              {v.blocker || 'Finish the preview before asking for approval.'}
            </span>
          )}
        </div>
      ) : null}

      {v.mode === 'waiting' ? (
        <div className="tp-form">
          <div>Waiting for approval. It needs approval because:</div>
          <Reasons v={v} />
          <Asked v={v} />
          <Deadline d={deadline} />
          {v.can_approve || v.can_reject ? (
            <>
              {!v.can_approve && v.blocker ? (
                <div className="tp-banner tp-banner-warn" role="note">
                  {v.blocker}
                </div>
              ) : null}
              <NoteField
                id="rx-review-note"
                label="Note for the person who asked"
                value={note}
                on_change={setNote}
                error={error}
                show_error={tried}
                input_ref={note_ref}
              />
              <div className="tp-modal-ft">
                <button
                  type="button"
                  className="tp-btn-out"
                  disabled={!!busy || !v.can_reject}
                  onClick={() => {
                    if (note_ok()) setConfirm('reject');
                  }}
                >
                  {busy === 'reject' ? 'Rejecting…' : 'Reject'}
                </button>
                <button
                  type="button"
                  className="tp-btn-out"
                  disabled={!!busy || !v.can_approve}
                  onClick={() => submit(on_approve)}
                >
                  {busy === 'approve' ? 'Approving…' : 'Approve'}
                </button>
                {on_approve_and_save ? (
                  <button
                    type="button"
                    className="tp-btn"
                    disabled={!!busy || !v.can_approve}
                    onClick={() => submit(on_approve_and_save)}
                  >
                    {busy === 'approve_save' ? 'Approving and saving…' : 'Approve and save'}
                  </button>
                ) : null}
              </div>
            </>
          ) : (
            <span className="tp-hint" data-testid="rx-approval-blocker">
              {v.is_maker
                ? 'You prepared this change or asked for its approval, so another teacher-payments user must approve it.'
                : v.blocker}
            </span>
          )}
          {withdraw ? <div className="tp-modal-ft">{withdraw}</div> : null}
        </div>
      ) : null}

      {v.mode === 'approved' ? (
        <div className="tp-form">
          <div className="tp-banner tp-banner-ok" role="status">
            Approved. Anyone with teacher payments access can now save it, while its preview is
            under 24 hours old. Re-running the preview cancels the approval.
          </div>
          <Deadline d={deadline} />
          <Reasons v={v} />
          <Asked v={v} />
          <Reviewed v={v} />
          {withdraw || v.can_cancel_approval ? (
            <div className="tp-modal-ft">
              {v.can_cancel_approval ? (
                <button
                  type="button"
                  className="tp-btn-out"
                  disabled={!!busy}
                  onClick={() => on_cancel_approval()}
                >
                  {busy === 'cancel_approval' ? 'Cancelling…' : 'Cancel my approval'}
                </button>
              ) : null}
              {withdraw}
            </div>
          ) : null}
        </div>
      ) : null}

      {v.mode === 'rejected' || v.mode === 'withdrawn' || v.mode === 'applied' ? (
        <div className="tp-form">
          {v.mode === 'rejected' ? (
            <div className="tp-banner tp-banner-error" role="note">
              Rejected. Nothing was saved. Prepare a new change if it is still needed.
            </div>
          ) : null}
          {v.mode === 'withdrawn' ? (
            <div className="tp-banner tp-banner-info" role="note">
              {v.withdrawn_line}
            </div>
          ) : null}
          <Asked v={v} />
          <Reviewed v={v} />
        </div>
      ) : null}

      {confirm ? (
        <Confirm
          copy={confirm_copy(confirm, v.asker_name)}
          on_keep={() => setConfirm('')}
          on_go={go}
        />
      ) : null}
    </section>
  );
};

export default ApprovalCard;
